import { Div, Text } from "atomize"
import { useContext } from "react"
import { DateTime } from "luxon"
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faComment } from "@fortawesome/free-regular-svg-icons"
import { theme } from "../settings/config"
import { sharedDataContext } from "../providers/sharedData.provider"
import {
	getLessonColor,
	getLessonPartecipant,
	getPlayerFullName,
	lessonTypeDisplayNameOf,
} from "../utils/func.utils"
import { isCancelledLesson } from "../utils/cancellation.utils"
import { paidAtSchool } from "../utils/billing.utils"
import { playerNoteOf } from "../utils/lessonContent.utils"
import { isRecoveredElsewhere, substituteKey } from "../utils/substitutes.utils"

// Riga compatta dello storico lezioni di un allievo: quando è successo,
// che lezione era e come ha pagato, e al posto di chi se è venuto da
// sostituto. Sotto, se il maestro l'ha scritta a fine lezione, la riga su di
// lui (src/modals/LessonDiaryModal.js).
// `noteBackground` è il fondo della riga sull'allievo: bianca nel blocco
// caldo delle lezioni, calda nel foglio bianco con tutte le lezioni.
function PlayerLessonRow({ lessonData, playerData, onClick, noteBackground = theme.colors.surface }) {
	const { getLessonType, getPaymentMethod } = useContext(sharedDataContext)

	const partecipant = getLessonPartecipant(
		lessonData,
		playerData.playerID,
		getPlayerFullName(playerData),
	)
	const lessonStart = lessonData.lessonStart?.toDate
		? DateTime.fromJSDate(lessonData.lessonStart.toDate())
		: null
	const isCancelled = isCancelledLesson(lessonData)
	const isAbsent = partecipant?.isPresent === false
	const note = playerNoteOf(lessonData.lessonContent, playerData.playerID)
	// venuto da sostituto, di solito per un recupero: al posto di chi
	const replaced = partecipant?.substituteFor
		? (lessonData.partecipants || []).find(
				player => !player.substituteFor && substituteKey(player) === partecipant.substituteFor,
			)
		: null

	const getPaymentLabel = () => {
		if (!partecipant) return ""
		// in una lezione annullata chi pagava la volta non paga niente
		if (isCancelled && !partecipant.paymentMethod) return ""
		if (partecipant.paymentMethod === "subscription") return "Abbonamento"
		if (partecipant.paymentMethod === "recovery") return "Recupero"
		if (paidAtSchool(partecipant, lessonData))
			return `${partecipant.paymentAmount ?? 0}€ · al circolo`
		if (!partecipant.paymentMethod) return `${partecipant.paymentAmount ?? 0}€ da segnare`
		return `${partecipant.paymentAmount ?? 0}€ · ${
			getPaymentMethod(partecipant.paymentMethod)?.paymentName ?? partecipant.paymentMethod
		}`
	}

	return (
		<motion.div whileTap={onClick && { scale: 0.98 }}>
			<Div
				d="flex"
				align="center"
				justify="space-between"
				p={{ y: "0.5rem" }}
				style={{ gap: "0.75rem", cursor: onClick ? "pointer" : "default" }}
				onClick={onClick}>
				<Div d="flex" align="center" style={{ gap: "0.75rem", minWidth: 0 }}>
					<Div
						w="0.5rem"
						h="2.25rem"
						rounded="circle"
						bg={getLessonColor(getLessonType(lessonData.lessonType), true)}
					/>
					<Div style={{ minWidth: 0 }}>
						<Text textColor={theme.colors.gunmetal} textWeight="bold">
							{lessonStart
								? lessonStart.setLocale("it").toFormat("ccc d LLL yyyy")
								: "Data non disponibile"}
						</Text>
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{lessonStart && `${lessonStart.toFormat("HH:mm")} · `}
							{lessonTypeDisplayNameOf(
								getLessonType(lessonData.lessonType),
								lessonData.lessonType,
							)}
						</Text>
					</Div>
				</Div>
				<Div textAlign="right" style={{ flexShrink: 0 }}>
					<Text textColor={theme.colors.gunmetal}>{getPaymentLabel()}</Text>
					{isRecoveredElsewhere(partecipant) ? (
						// recuperata fuori dall'app: l'assenza c'è, ma è chiusa
						<Text textSize="caption" textColor={theme.colors.green}>
							{isCancelled ? "Annullata · " : "Assente · "}recuperata altrove
						</Text>
					) : isCancelled ? (
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{partecipant?.paymentMethod === "subscription"
								? "Annullata · da recuperare"
								: "Annullata"}
						</Text>
					) : isAbsent ? (
						<Text textSize="caption" textColor={theme.colors.red}>
							Assente
						</Text>
					) : (
						replaced?.playerName && (
							<Text textSize="caption" textColor={theme.colors.text_light}>
								al posto di {replaced.playerName.trim()}
							</Text>
						)
					)}
				</Div>
			</Div>
			{note && (
				<Div
					// allineata alla data, oltre la tacca del colore
					m={{ l: "1.25rem", b: "0.375rem", t: "-0.25rem" }}
					p="0.5rem 0.75rem"
					bg={noteBackground}
					rounded="15px"
					d="flex"
					align="baseline"
					style={{ gap: "0.5rem" }}>
					<FontAwesomeIcon icon={faComment} color={theme.colors.text_light} size="sm" />
					<Text textSize="caption" textColor={theme.colors.gunmetal} style={{ minWidth: 0 }}>
						{note}
					</Text>
				</Div>
			)}
		</motion.div>
	)
}

export default PlayerLessonRow
